// Figma: "Loyalty Cloud" file, node 18093:4593/18093:4594 — avatar dropdown menu
// (name, email, Account settings / Theme / Invite team members / Home page / Log out).
import React from 'react';
import PropTypes from 'prop-types';
import {useNavigate} from 'react-router-dom';
import {useDispatch} from 'react-redux';
import {Box, ClickAwayListener, Popper, Stack, Typography} from '@mui/material';

import {tokens} from '../../themes/ThemeCustomization';
import {resetSession} from '../../app/store/reducers/session';
import accountSettingsIcon from '../../assets/icons/header/account-settings.svg';
import inviteTeamIcon from '../../assets/icons/header/invite-team.svg';
import homeIcon from '../../assets/icons/header/home.svg';
import logoutIcon from '../../assets/icons/header/logout.svg';

const menuItemSx = {
    height: '36px',
    px: 1,
    gap: '12px',
    borderRadius: tokens.radius.control,
    cursor: 'pointer',
    '&:hover': {backgroundColor: tokens.color.neutral50}
};

const MenuItem = ({icon, label, trailingIcon, onClick}) => (
    <Stack direction="row" alignItems="center" sx={menuItemSx} onClick={onClick}>
        <Box component="img" src={icon} alt="" sx={{width: 16, height: 16}} />
        <Typography variant="body2" sx={{flex: trailingIcon ? 1 : 'none', fontSize: '14px', color: tokens.color.neutral950}}>
            {label}
        </Typography>
        {trailingIcon && <Box component="img" src={trailingIcon} alt="" sx={{width: 16, height: 16}} />}
    </Stack>
);

MenuItem.propTypes = {
    icon: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    trailingIcon: PropTypes.string,
    onClick: PropTypes.func
};

const ProfileMenu = ({anchorEl, open, onClose, firstName, lastName, email}) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const fullName = [firstName, lastName].filter(Boolean).join(' ') || 'Team member';

    const handleLogout = () => {
        dispatch(resetSession());
        onClose();
        navigate('/login', {replace: true});
    };

    const handleHome = () => {
        onClose();
        navigate('/');
    };

    const handleAccountSettings = () => {
        onClose();
        navigate('/settings/profile');
    };

    const handleInvite = () => {
        onClose();
        navigate('/settings/members?invite=1');
    };

    return (
        <Popper open={open} anchorEl={anchorEl} placement="bottom-end" sx={{zIndex: 1300}} modifiers={[{name: 'offset', options: {offset: [0, 8]}}]}>
            <ClickAwayListener onClickAway={onClose}>
                <Box
                    sx={{
                        width: '240px',
                        backgroundColor: tokens.color.white,
                        border: `1px solid ${tokens.color.neutral200}`,
                        borderRadius: tokens.radius.card,
                        boxShadow: tokens.shadow.card,
                        p: '6px'
                    }}
                >
                    <Box sx={{p: 1, borderBottom: `1px solid ${tokens.color.neutral200}`}}>
                        <Typography sx={{fontSize: '14px', fontWeight: 500, lineHeight: '20px', color: tokens.color.neutral950}}>{fullName}</Typography>
                        <Typography sx={{fontSize: '12px', lineHeight: '16px', color: tokens.color.neutral600}}>{email}</Typography>
                    </Box>

                    <MenuItem icon={accountSettingsIcon} label="Account settings" onClick={handleAccountSettings} />
                    <MenuItem icon={inviteTeamIcon} label="Invite team members" onClick={handleInvite} />
                    <MenuItem icon={homeIcon} label="Home page" onClick={handleHome} />
                    <MenuItem icon={logoutIcon} label="Log out" onClick={handleLogout} />
                </Box>
            </ClickAwayListener>
        </Popper>
    );
};

ProfileMenu.propTypes = {
    anchorEl: PropTypes.instanceOf(Element),
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string
};

export default ProfileMenu;
